/**
 * AdminTestimonials — moderation queue for the opt-in landing carousel.
 *
 * Left column: filter tabs (Pending / Approved / Rejected).
 * Table:      display names, quote preview, submitter, submitted date,
 *             quick approve / reject actions. Approving flips the row
 *             live to the public /testimonials/public feed.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Check, X } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';

const TABS = [
  ['pending',  'Pending'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
];

export default function AdminTestimonials() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get('/admin/me').then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/testimonials?status=${tab}`);
      setRows(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load testimonials');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/testimonials/${id}/approve`);
      toast.success('Approved · will appear on landing');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusyId(null); }
  };
  const reject = async (id) => {
    const note = window.prompt('Rejection note (optional, visible in DB only):', '') || '';
    setBusyId(id);
    try {
      await api.post(`/admin/testimonials/${id}/reject`, { note });
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
    finally { setBusyId(null); }
  };

  if (!authChecked || (loading && rows.length === 0)) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;
  }

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="admin-testimonials-page">
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Testimonials</h1>
        <button onClick={load} disabled={loading} data-testid="testimonials-refresh"
          className="ml-auto p-2 rounded-full hover:bg-black/[0.04]" aria-label="Refresh">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="px-5 pt-4">
        <div className="inline-flex gap-1 rounded-full border border-[var(--hairline)] p-1 bg-[var(--surface-2)]">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={`testimonials-tab-${k}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                tab === k ? 'bg-[#0F0F10] text-white' : 'text-[#4B4B52] hover:text-[#0F0F10]'
              }`}>
              {label} <span className="tabular-nums opacity-70 ml-1">{counts[k] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 mt-4">
        <div className="rounded-2xl border border-[var(--hairline)] overflow-hidden">
          <table className="w-full text-sm" data-testid="testimonials-table">
            <thead className="bg-[var(--surface-2)]">
              <tr className="text-[10px] uppercase tracking-wider text-[#4B4B52]">
                <th className="text-left px-3 py-2">Attribution</th>
                <th className="text-left px-3 py-2">Story</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Submitter</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#8A8A93] italic text-xs">No testimonials with this status.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--hairline)] align-top" data-testid={`testimonial-row-${r.id}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {r.photo_url ? (
                        <img src={r.photo_url.startsWith('http') ? r.photo_url : `/api/files/${r.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : <div className="w-9 h-9 rounded-full bg-black/[0.06]" />}
                      <div>
                        <p className="font-medium text-[#0F0F10]">{r.display_names}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#8A8A93]">{r.relationship_status}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-[13px] text-[#4B4B52] max-w-md">&ldquo;{r.quote}&rdquo;</td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider ${
                      r.status === 'approved' ? 'bg-emerald-100 text-emerald-800'
                      : r.status === 'rejected' ? 'bg-rose-100 text-rose-800'
                      : 'bg-amber-100 text-amber-800'
                    }`}>{r.status.toUpperCase()}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-[#4B4B52]">
                    <div>{r.submitter?.name || '—'}</div>
                    <div className="text-[10px] text-[#8A8A93]">{r.submitter?.email || r.user_id}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-[#4B4B52] tabular-nums whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {r.status !== 'approved' && (
                        <button onClick={() => approve(r.id)} disabled={busyId === r.id}
                          data-testid={`approve-${r.id}`}
                          className="p-1.5 rounded-md text-emerald-700 hover:bg-emerald-50" aria-label="Approve">
                          <Check size={14} />
                        </button>
                      )}
                      {r.status !== 'rejected' && (
                        <button onClick={() => reject(r.id)} disabled={busyId === r.id}
                          data-testid={`reject-${r.id}`}
                          className="p-1.5 rounded-md text-rose-700 hover:bg-rose-50" aria-label="Reject">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
